<template>
  <b-dropdown
    :triggers="['hover']"
    aria-role="list"
    :position="dropdownPosition"
    @mouseenter.native="updateDropdownPosition"
  >
    <template #trigger>
      <b-button
        :type="{
          'is-white': t.details.status === 'open',
          'is-success': t.details.status === 'success',
          'is-smart-orange-7': t.details.status === 'refused',
          'is-light': t.details.status === 'cancelled',
          'is-warning':
            t.details.status === 'error' ||
            t.details.status === 'toValidate',
          'is-dark': t.details.status === 'refunded',
          'is-danger': t.details.status === 'chargeback',
          'is-sea': t.details.status === 'pending',
          'is-light-green':
            t.details.status === 'billingPending' ||
            t.details.status === 'chargebackPaid'
        }"
        :disabled="t.details.status === 'cancelled'"
        slot="trigger"
      >
        {{
          t.details.status === "success"
            ? $t("transactions.statusSuccess")
            : t.details.status === "refused"
            ? $t("transactions.statusRefused")
            : t.details.status === "error"
            ? $t("transactions.statusError")
            : t.details.status === "toValidate"
            ? $t("transactions.statusToValidate")
            : t.details.status === "cancelled"
            ? $t("transactions.statusCancelled")
            : t.details.status === "open"
            ? $t("transactions.statusOpen")
            : t.details.status === "pending"
            ? $t("transactions.statusPending")
            : t.details.status === "refundedRequested"
            ? $t("transactions.statusRefundRequested")
            : t.details.status === "refunded"
            ? $t("transactions.statusRefunded")
            : t.details.status === "refundRefused"
            ? $t("transactions.statusRefundRefused")
            : t.details.status === "refundFailed"
            ? $t("transactions.statusRefundFailed")
            : t.details.status === "refundReversed"
            ? $t("transactions.statusRefundReversed")
            : t.details.status === "chargeback"
            ? $t("transactions.statusChargeback")
            : t.details.status === "chargebackPaid"
            ? $t("transactions.statusChargebackPaid")
            : "-"
        }}
      </b-button>
    </template>

    <div v-if="t.details.status !== 'cancelled'">
      <b-dropdown-item aria-role="listitem">
        <p>{{ paymentMethods }}</p>
      </b-dropdown-item>

      <b-dropdown-item aria-role="listitem" v-if="cardBrand">
        <b>{{ $t("transactions.cardBrand") }}: </b>
        {{ cardBrand }}
      </b-dropdown-item>

      <hr class="separator has-background-primary" />

      <b-dropdown-item aria-role="listitem">
        <b>{{ $t("transactions.cart") }}: </b>
        {{
          `${getCurrencySymbolBasedOnCurrency(t.details.currency)}${formatMoney(
            t.cart.totalValue
          )}`
        }}
      </b-dropdown-item>

      <div
        v-if="
          t.discounts &&
            ((t.discounts.coupon && t.discounts.coupon.total) ||
              t.discounts.cashbackUsed)
        "
      >
        <b-dropdown-item
          aria-role="listitem"
          v-if="t.discounts.coupon && t.discounts.coupon.total"
        >
          <b>(-) {{ $t("transactions.coupon") }}: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.discounts.coupon.total)}`
          }}
          <br />
          <small>
            {{
              t.discounts.coupon.type === "absolute"
                ? `--> ${
                    t.discounts.coupon.name
                  } ${getCurrencySymbolBasedOnCurrency(
                    t.details.currency
                  )}${formatMoney(t.discounts.coupon.value)}`
                : t.discounts.coupon.type === "percentual"
                ? `--> ${t.discounts.coupon.name} ${t.discounts.coupon.value}%`
                : "?"
            }}
          </small>
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem" v-if="t.discounts.cashbackUsed">
          <b>(-) {{ $t("transactions.cashback") }}: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.discounts.cashbackUsed)}`
          }}
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <hr class="bill-separator has-background-elegant-black-3" />
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <b>{{ $t("transactions.totalToPay") }}: </b>
          {{
            `${getCurrencySymbolBasedOnCurrency(
              t.details.currency
            )}${formatMoney(t.details.chargedValue)}`
          }}
        </b-dropdown-item>
      </div>

      <b-dropdown-item
        aria-role="listitem"
        v-if="
          t.details.status === 'toValidate' &&
            permissions.markTefTransactionAsPaid
        "
      >
        <b-button type="is-success" @click="$emit('mark-as-paid', t)">
          {{ $t("transactions.markAsPaid") }}
        </b-button>
      </b-dropdown-item>
    </div>
    <dropdown-item-combo :t="t" />
  </b-dropdown>
</template>

<script>
import { mapGetters } from "vuex";
import { mask } from "../../../mixins/mask";
import { getSitefVoucherBrand } from "../../../utils/sitefVoucherBrands";
import dropdownItemCombo from "./DropdownItemCombo.vue";

export default {
  components: {
    dropdownItemCombo
  },

  mixins: [mask],

  props: ["t"],

  data() {
    return {
      dropdownPosition: "is-bottom-left"
    };
  },

  methods: {
    // Nas ultimas linhas da tabela o dropdown aberto para baixo fica cortado
    // pelo fim da tela. O menu so tem altura depois que o hover o abre, entao
    // ele comeca para baixo e vira para cima no frame seguinte se nao couber.
    updateDropdownPosition() {
      this.dropdownPosition = "is-bottom-left";
      this.flipDropdownIfClipped(2);
    },

    flipDropdownIfClipped(remainingFrames) {
      window.requestAnimationFrame(() => {
        const menu = this.$el.querySelector(".dropdown-menu");

        if (!menu) return;

        const { height, bottom } = menu.getBoundingClientRect();

        if (!height) {
          if (remainingFrames > 0)
            this.flipDropdownIfClipped(remainingFrames - 1);
          return;
        }

        if (bottom > window.innerHeight) {
          this.dropdownPosition = "is-top-left";
        }
      });
    }
  },

  computed: {
    ...mapGetters(["permissions"]),

    paymentMethods() {
      let p = [];

      if (this.t.details.fundingSource === "credit") {
        p.push(this.$t("transactions.credit"));
      }

      if (this.t.details.fundingSource === "debit") {
        p.push(this.$t("transactions.debit"));
      }

      if (this.t.details.fundingSource === "voucher") {
        p.push(this.$t("transactions.voucher"));
      }

      if (this.t.details.fundingSource === "pix") {
        p.push(this.$t("transactions.pix"));
      }

      return p.join(" + ");
    },

    // A tabela do SiTef que temos mapeada cobre apenas os codigos de voucher,
    // por isso a bandeira so e exibida nessa forma de pagamento.
    cardBrand() {
      if (this.t.details.fundingSource !== "voucher") {
        return null;
      }

      const response = this.t.tefTerminal && this.t.tefTerminal.response;

      return response ? getSitefVoucherBrand(response.processTypeCard) : null;
    }
  }
};
</script>

<style scoped>
.separator {
  height: 1px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.bill-separator {
  height: 1px;
  margin-top: 0.1rem;
  margin-bottom: 0.1rem;
}
.is-white,
.is-white:hover {
  border: 1px solid #dedede;
}
</style>
